/**
 * Who is in the room, and whether each one is working right now.
 *
 * Not a roster the human keeps honest: nobody is "in" or "out". A persona is
 * woken when its work exists. What the human controls is whether it is staffed
 * at all (add / deactivate), and how many of an archetype there are.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { BoardroomArchetype, BoardroomPersona } from '../../types';
import { colorsByOrder } from './seatColors';
import { sourceLabel, workLine, writeCount, writesLabel, writesTitle, writesTone } from './personaWork';

function paletteFor(personas: BoardroomPersona[]) {
  return colorsByOrder(personas.map(p => p.id));
}

/** Tokens in a few characters: 372k, 13.4M. */
export function tokensLabel(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

/** One kind of seat, added up. Pure, so the sums have tests. */
export interface SeatGroup {
  archetype: string;
  seats: BoardroomPersona[];
  active: number;
  working: number;
  queued: number;
  tokens: number;
  turns: number;
  writes: Record<string, number>;
  /** Every seat of this kind is idle with nothing to do. */
  nothingLeft: boolean;
}

export function groupSeats(personas: BoardroomPersona[]): SeatGroup[] {
  const by: Record<string, BoardroomPersona[]> = {};
  personas.forEach(p => {
    (by[p.archetype] = by[p.archetype] ?? []).push(p);
  });
  return Object.keys(by)
    .sort((a, b) => a.localeCompare(b))
    .map(archetype => {
      const seats = by[archetype];
      const sorted = [...seats].sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
      const writes: Record<string, number> = {};
      seats.forEach(s => {
        const w: Record<string, number> = s.writes ?? {};
        Object.keys(w).forEach(tool => {
          writes[tool] = (writes[tool] ?? 0) + (Number(w[tool]) || 0);
        });
      });
      const live = seats.filter(s => s.is_active);
      return {
        archetype,
        seats: sorted,
        active: live.length,
        working: live.filter(s => s.state === 'working').length,
        queued: live.filter(s => s.state === 'queued').length,
        tokens: seats.reduce((a, s) => a + (s.tokens_spent || 0), 0),
        turns: seats.reduce((a, s) => a + (s.turns_done || 0), 0),
        writes,
        nothingLeft: live.length > 0 && live.every(s => s.state === 'nothing_left'),
      };
    });
}

export default function PersonaStrip({
  boardroomId,
  personas,
  onChanged,
  byCounts = null,
  activeBy = null,
  onBy,
}: {
  boardroomId: number;
  personas: BoardroomPersona[];
  onChanged: (persona: BoardroomPersona) => void;
  /** Items per author under the current lens: `"human"` or a persona id. */
  byCounts?: Record<string, number> | null;
  /** The author the feed is narrowed to, if any. */
  activeBy?: string | null;
  /** Click an author's count to narrow the feed to what they wrote. */
  onBy?: (by: string) => void;
}) {
  const countChip = (key: string) => {
    if (!byCounts || !onBy) return null;
    const n = byCounts[key] ?? 0;
    const active = activeBy === key;
    return (
      <button
        type="button"
        onClick={() => onBy(key)}
        title={active ? 'Showing only their items - click to clear' : 'Show only their items'}
        className={`ml-1 px-1.5 py-0.5 rounded-full border text-[11px] tabular-nums ${
          active
            ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200'
            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
        }`}
        data-testid={`by-${key}`}
      >
        {n} {n === 1 ? 'item' : 'items'}
      </button>
    );
  };
  const [archetypes, setArchetypes] = useState<BoardroomArchetype[]>([]);
  const [busy, setBusy] = useState(false);
  // ONE ROW PER KIND OF SEAT. With copies, the room grew to forty rows of
  // "Competitor miner 6 · competitor miner" — every seat, its own numbers,
  // and no way to see what the room as a whole was doing. A kind's row adds
  // its seats up; open it to see (and cross out, or filter by) each one.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggleOpen = (kind: string) =>
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  // Colours stay keyed on staffing order (a persona keeps its colour for life).
  const colors = paletteFor(personas);
  const groups = groupSeats(personas);
  const kindName = (kind: string) =>
    archetypes.find(a => a.archetype === kind)?.name ??
    kind.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
  const itemsOf = (g: SeatGroup) =>
    byCounts ? g.seats.reduce((a, s) => a + (byCounts[String(s.id)] ?? 0), 0) : null;

  useEffect(() => {
    api.listBoardroomArchetypes().then(setArchetypes).catch(() => setArchetypes([]));
  }, []);

  const toggle = async (p: BoardroomPersona) => {
    if (busy) return;
    setBusy(true);
    try {
      onChanged(await api.updateBoardroomPersona(boardroomId, p.id, { is_active: !p.is_active }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-sm">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Seats</div>
      {personas.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Nobody staffed yet.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {groups.map(g => {
            const isOpen = open.has(g.archetype);
            const items = itemsOf(g);
            const lead = colors.get(g.seats[0].id);
            return (
              <li key={g.archetype} className="text-xs" data-testid={`seat-kind-${g.archetype}`}>
                <button
                  type="button"
                  onClick={() => toggleOpen(g.archetype)}
                  aria-expanded={isOpen}
                  title={isOpen ? 'Hide the seats' : 'Show each seat'}
                  className="w-full flex items-center gap-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-0.5"
                >
                  <span className="w-2 text-gray-400" aria-hidden>{isOpen ? '▾' : '▸'}</span>
                  <span className={`inline-block w-2 h-2 rounded-full ${lead?.dot ?? 'bg-gray-400'}`} aria-hidden />
                  <span className={`flex-1 min-w-0 truncate ${g.active ? 'text-gray-800 dark:text-gray-100' : 'text-gray-400 line-through'}`}>
                    {kindName(g.archetype)}
                  </span>
                  <span className="text-gray-400 tabular-nums" title="seats staffed" data-testid={`seat-kind-count-${g.archetype}`}>
                    ×{g.active}
                  </span>
                  <span className="w-16 text-right tabular-nums" data-testid={`seat-kind-state-${g.archetype}`}>
                    {g.working ? (
                      <span className="text-amber-600 dark:text-amber-300 animate-pulse">{g.working} working</span>
                    ) : g.queued ? (
                      <span className="text-gray-400" title="Turns on the queue, waiting for a worker">{g.queued} queued</span>
                    ) : g.nothingLeft ? (
                      <span className="text-gray-400 italic" title="No work for this kind of seat; it wakes when some appears">nothing left</span>
                    ) : null}
                  </span>
                  <span className="w-12 text-right text-gray-400 tabular-nums" title="tokens spent, all seats">
                    {tokensLabel(g.tokens)}
                  </span>
                  <span
                    className={`w-16 text-right tabular-nums ${writesTone(g.writes, g.turns) === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                    title={writesTitle(g.writes, g.turns)}
                    data-testid={`seat-kind-writes-${g.archetype}`}
                  >
                    {g.turns ? `${writeCount(g.writes)} writes` : ''}
                  </span>
                  {items !== null && (
                    <span className="w-14 text-right text-gray-500 dark:text-gray-400 tabular-nums" title="items written, all seats">
                      {items} {items === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <ul className="mt-0.5 mb-1 ml-4 flex flex-col gap-0.5 border-l border-gray-100 dark:border-gray-700 pl-2">
                    {g.seats.map(p => {
                      const c = colors.get(p.id);
                      return (
                        <li key={p.id} className="flex flex-wrap items-center gap-x-2" data-testid={`persona-${p.id}`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${c?.dot ?? 'bg-gray-400'}`} aria-hidden />
                          <button
                            type="button"
                            onClick={() => toggle(p)}
                            title={p.is_active ? 'Deactivate' : 'Activate'}
                            className={`text-left flex-1 ${p.is_active ? c?.text ?? '' : 'text-gray-400 line-through'}`}
                          >
                            {p.name}
                            {/* A seat the room added for itself. Marked so it can be
                                seen and put back — the cast is still the human's. */}
                            {p.added_by_run_id && (
                              <span
                                className="text-gray-400 no-underline"
                                title="the room added this seat for a queue its others could not cover - cross it out to remove it"
                                data-testid={`persona-added-${p.id}`}
                              >
                                {' '}·added
                              </span>
                            )}
                          </button>
                          {p.state === 'working' ? (
                            <span className="text-amber-600 dark:text-amber-300 animate-pulse">working</span>
                          ) : p.state === 'queued' ? (
                            <span className="text-gray-400" title="Turn is on the queue; waiting for a worker">
                              queued
                            </span>
                          ) : p.state === 'nothing_left' ? (
                            <span className="text-gray-400 italic" title="The board has no work this persona does; it wakes when some appears">
                              nothing left
                            </span>
                          ) : null}
                          <span className="text-gray-400 tabular-nums" title="tokens spent">
                            {tokensLabel(p.tokens_spent)}
                          </span>
                          {writesLabel(p.writes, p.turns_done) && (
                            <span
                              className={`tabular-nums ${
                                writesTone(p.writes, p.turns_done) === 'bad'
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-gray-400'
                              }`}
                              title={writesTitle(p.writes, p.turns_done)}
                              data-testid={`persona-writes-${p.id}`}
                            >
                              {writesLabel(p.writes, p.turns_done)}
                            </span>
                          )}
                          {countChip(String(p.id))}
                          {/* WHAT IT IS DOING, not just that it is doing something. */}
                          {workLine(p.assignment) && (
                            <span
                              className="basis-full text-[11px] text-gray-500 dark:text-gray-400 pl-3"
                              data-testid={`persona-work-${p.id}`}
                              title={`${p.assignment!.text}\n\n(${sourceLabel(p.assignment!)})`}
                            >
                              {workLine(p.assignment)}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {byCounts && onBy && (byCounts.human ?? 0) > 0 && (
        <div className="mt-1 flex items-center gap-2 text-xs" data-testid="persona-human">
          <span className="inline-block w-2 h-2 rounded-full bg-gray-400" aria-hidden />
          <span className="flex-1 text-gray-700 dark:text-gray-200">
            You<span className="text-gray-400"> · by hand</span>
          </span>
          {countChip('human')}
        </div>
      )}
    </div>
  );
}
